/**
 * useCompleteInspection — supervisor-side mutation that calls the
 * complete-inspection Edge Function. Closes the active inspection
 * pass with a terminal decision. Surfaces structured error codes
 * from the Edge Function + RPC as AppError.code (CLAUDE.md ERROR 4).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export type CompleteInspectionDecision =
  | 'passed'
  | 'failed'
  | 're_clean_required'
  | 'blocked_by_maintenance'
  | 'blocked_by_owner_approval'
  | 'cancelled'

export interface CompleteInspectionVars {
  inspectionId:        string
  /** Used for query invalidation only. */
  cleaningTaskId:      string
  decision:            CompleteInspectionDecision
  failureReason?:      string
  photoStoragePaths?:  string[]
  notes?:              string
}

export interface CompleteInspectionResult {
  inspection_id: string
  status:        Exclude<CompleteInspectionDecision, 'passed'> | 'passed'
  pass_number:   number
}

export function useCompleteInspection(): UseMutationResult<CompleteInspectionResult, AppError, CompleteInspectionVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<CompleteInspectionResult, AppError, CompleteInspectionVars>({
    mutationFn: async (vars): Promise<CompleteInspectionResult> => {
      const { data, error } = await supabase.functions.invoke<CompleteInspectionResult>('complete-inspection', {
        body: {
          inspection_id:        vars.inspectionId,
          decision:             vars.decision,
          failure_reason:       vars.failureReason,
          photo_storage_paths:  vars.photoStoragePaths,
          notes:                vars.notes,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'REQUEST_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Complete-inspection returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['inspections'] })
      void queryClient.invalidateQueries({ queryKey: ['inspections', vars.cleaningTaskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'FORBIDDEN_ROLE':           return 'Only supervisors can complete an inspection.'
    case 'FORBIDDEN_ORG':            return 'This inspection does not belong to your organization.'
    case 'INSPECTION_NOT_FOUND':     return 'This inspection could not be found.'
    case 'INVALID_STATE':            return 'This inspection is not in a state where it can be completed.'
    case 'INVALID_DECISION':         return 'Pick a valid outcome before submitting.'
    case 'INVALID_FAILURE_REASON':   return 'A failure reason is required for any outcome other than passed.'
    case 'INVALID_NOTES':            return 'Notes must be under 1,000 characters.'
    case 'TOO_MANY_PHOTOS':          return 'At most 10 photos can be attached to a single inspection.'
    case 'INVALID_PHOTO_PATHS':      return 'Photo references must be non-empty strings under 500 characters.'
    case 'RATE_LIMITED':             return 'Too many submissions in a row. Try again in a moment.'
    default:                         return detail || 'Could not complete the inspection.'
  }
}
